// File: src/components/host/modals/PortalLoginModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { getPortalAccess, rememberPortalAccessHandoff, clearPortalAccessHandoff } from '../../../utils/portalAccess';

export default function PortalLoginModal({ open, onClose }) {
  const api = process.env.REACT_APP_API_URL;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const scrollRef = useRef(0);

  useEffect(() => {
    if (!open) return;

    const meta = document.querySelector('meta[name="theme-color"]');

    scrollRef.current = window.scrollY;
    document.body.style.cssText = `position:fixed;top:-${scrollRef.current}px;width:100%;`;
    document.body.classList.add('overlay-active');
    document.documentElement.classList.add('overlay-active');
    document.getElementById('root')?.classList.add('overlay-active');

    if (meta) meta.content = '#0b0b0b';

    return () => {
      document.body.style.cssText = '';
      window.scrollTo(0, scrollRef.current);

      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root')?.classList.remove('overlay-active');

      if (meta) meta.content = '#ffffff';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    setErr('');
    setLoading(false);
  }, [open]);

  const canSubmit = useMemo(() => {
    return (
      String(email).trim().length > 0 &&
      String(password).length > 0 &&
      !loading
    );
  }, [email, password, loading]);

  async function tryLogin(endpoint) {
    const res = await axios.post(`${api}${endpoint}`, {
      email: String(email).trim().toLowerCase(),
      password: String(password),
    });

    const token =
      res.data?.accessToken ||
      res.data?.token ||
      res.data?.authToken ||
      res.data?.data?.accessToken ||
      res.data?.data?.token ||
      '';

    const refreshToken =
      res.data?.refreshToken ||
      res.data?.data?.refreshToken ||
      '';

    const sessionId =
      res.data?.sessionId ||
      res.data?.data?.sessionId ||
      '';

    const user =
      res.data?.user ||
      res.data?.data?.user ||
      res.data?.currentUser ||
      null;

    const userId =
      user?._id ||
      res.data?.userId ||
      res.data?.id ||
      res.data?.data?.userId ||
      '';

    return {
      token,
      refreshToken,
      sessionId,
      user,
      userId,
    };
  }

  function saveSession({ token, refreshToken, sessionId, userId }) {
    localStorage.setItem('accessToken', token);
    localStorage.setItem('token', token);
    localStorage.setItem('authToken', token);

    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }

    if (sessionId) {
      localStorage.setItem('sessionId', sessionId);
    }

    if (userId) {
      localStorage.setItem('userId', String(userId));
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setErr('');
    setLoading(true);

    try {
      let result;

      try {
        result = await tryLogin('/auth/login');
      } catch {
        result = await tryLogin('/users/login');
      }

      const {
        token,
        refreshToken,
        sessionId,
        userId,
      } = result || {};

      if (!token) {
        throw new Error('Login failed (missing token).');
      }

      clearPortalAccessHandoff();
      const access = await getPortalAccess(token);
      if (!access.hasHostAccess && !access.hasReferralPartnerAccess) {
        throw new Error('This account does not have access to a Tribe portal.');
      }

      saveSession({
        token,
        refreshToken,
        sessionId,
        userId,
      });

      // Everyone keeps the portal selector. Hand the verified access flags
      // through once so it does not make a duplicate request after login.
      rememberPortalAccessHandoff(token, access);
      window.location.replace('/portal');
    } catch (e2) {
      setErr(
        e2?.response?.data?.message ||
        e2?.message ||
        'Login failed.'
      );

      setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div
      style={M.backdrop}
      role="presentation"
      onClick={() => onClose?.()}
    >
      <div
        style={M.sheet}
        role="dialog"
        aria-modal="true"
        aria-label="Portal login"
        onClick={(e) => e.stopPropagation()}
      >
        <div style={M.header}>
          <div style={M.headerLeft}>
            <div style={M.headerTitle}>Portal login</div>
          </div>

          <div style={M.headerCenter} />

          <div style={M.headerRight}>
            <button
              type="button"
              onClick={() => onClose?.()}
              style={M.closeBtn}
            >
              ×
            </button>
          </div>
        </div>

        <div style={M.body}>
          <div style={M.headline}>Log in</div>

          <div style={M.sub}>
            Access your Tribe host or Brand Ambassador portal.
          </div>

          <form onSubmit={onSubmit} style={{ marginTop: 14 }}>
            <div style={M.label}>Email</div>

            <input
              style={M.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              autoComplete="email"
            />

            <div style={{ ...M.label, marginTop: 10 }}>
              Password
            </div>

            <input
              style={M.input}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              type="password"
              autoComplete="current-password"
            />

            {err ? <div style={M.err}>{err}</div> : null}

            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                ...M.btn,
                ...(canSubmit ? M.btnDark : M.btnDisabled),
              }}
            >
              {loading ? 'Logging in…' : 'Log in'}
            </button>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateY(10px);
            opacity: 0;
          }

          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation: none !important;
            transition: none !important;
          }
        }

        button:focus-visible {
          outline: 0;
          box-shadow: 0 0 0 3px rgba(59,130,246,.45);
        }
      `}</style>
    </div>
  );
}

const M = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.40)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 12,
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  sheet: {
    width: '100%',
    maxWidth: 380,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    animation: 'slideIn .22s ease-out',
    display: 'flex',
    flexDirection: 'column',
  },

  header: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    padding: '0px 16px',
    minHeight: 48,
    borderBottom: '1px solid #eef0f2',
    background: '#fff',
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minWidth: 0,
  },

  headerCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  headerRight: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },

  headerTitle: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: '0.06em',
    color: '#0b0b0b',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  logo: {
    width: 76,
    height: 34,
    display: 'block',
  },

  closeBtn: {
    background: 'transparent',
    border: 'none',
    color: '#374151',
    padding: 0,
    fontSize: 28,
    fontWeight: 400,
    lineHeight: 1,
    cursor: 'pointer',
  },

  body: {
    padding: '30px 16px',
  },

  headline: {
    margin: 0,
    fontSize: 30,
    fontWeight: 600,
    lineHeight: 1.05,
    letterSpacing: '-0.03em',
    color: '#0b0b0b',
  },

  sub: {
    marginTop: 8,
    fontSize: 13.5,
    color: '#4b5563',
    lineHeight: 1.45,
  },

  label: {
    fontSize: 12,
    fontWeight: 900,
    color: '#374151',
    marginBottom: 6,
    letterSpacing: 0.2,
  },

  input: {
    width: '100%',
    height: 44,
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    padding: '0 12px',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },

  err: {
    marginTop: 10,
    fontSize: 12,
    color: '#b91c1c',
    fontWeight: 800,
  },

  btn: {
    width: '100%',
    height: 44,
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    fontSize: 12,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.04,
    cursor: 'pointer',
    marginTop: 12,
  },

  btnDark: {
    background: '#0b0b0b',
    color: '#fff',
    border: '1px solid #0b0b0b',
  },

  btnDisabled: {
    background: '#e5e7eb',
    color: '#6b7280',
    cursor: 'not-allowed',
  },
};